import { useState, useEffect, useCallback } from "react";
import {
  useFloating,
  offset,
  flip,
  shift,
  size as sizeMw,
  autoUpdate,
} from "@floating-ui/react";

/**
 * Generic popover/menu hook – use for Selects, Menus, DatePickers, Tooltips (with tweaks).
 */
export function useFloatingMenu({
  initialOpen = false,
  placement = "bottom-start",
  sameWidth = true,       // match trigger width
  exactWidth = true,      // set width exactly (vs. only minWidth)
  minWidthPx = 240,       // minimum panel width
  strategy = "fixed",     // robust inside transformed containers
  offsetValue = 4,        // gap between trigger and panel
  flipOptions,            // pass through to flip()
  shiftOptions,           // pass through to shift()
  onOpenChange,           // notify parent when open changes
  maxHeight = 320,        // clamp panel height; scroll inside
} = {}) {
  const [open, _setOpen] = useState(initialOpen);

  const setOpen = useCallback(
    (next) => {
      const v = typeof next === "function" ? next(open) : next;
      _setOpen(v);
      onOpenChange?.(v);
    },
    [open, onOpenChange]
  );

  const { refs, floatingStyles, update, placement: currentPlacement } = useFloating({
    placement,
    strategy,
    whileElementsMounted: autoUpdate, // reposition on scroll/resize/content changes
    middleware: [
      offset(offsetValue),
      flip({ fallbackAxisSideDirection: "start", ...flipOptions }),
      shift({ padding: 8, ...shiftOptions }), // keep within viewport
      sizeMw({
        apply({ rects, availableHeight, availableWidth, elements }) {
          const refW = rects.reference.width;
          // desired width = at least minWidthPx, at least trigger width (if sameWidth),
          // but not larger than the available space inside the viewport.
          const desired = sameWidth ? Math.max(refW, minWidthPx) : minWidthPx;
          const clamped = Math.min(desired, availableWidth);

          // lock the width, or just enforce a floor
          if (exactWidth && sameWidth) {
            elements.floating.style.width = `${clamped}px`;
            elements.floating.style.minWidth = "";  // clean up any previous styles
            elements.floating.style.maxWidth = "";
          } else {
            elements.floating.style.minWidth = `${Math.min(
              Math.max(minWidthPx, 0),
              availableWidth
            )}px`;
          }

          elements.floating.style.maxHeight = `${Math.min(
            availableHeight,
            maxHeight
          )}px`;
          elements.floating.style.overflowY = "auto";
        },
      }),
    ],
  });

  // Outside click + Escape to close
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      const ref = refs.reference.current;
      const floatEl = refs.floating.current;
      if (ref?.contains(e.target) || floatEl?.contains(e.target)) return;
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, refs.reference, refs.floating, setOpen]);


  // Minimal default handlers you can spread on the trigger
  const referenceProps = {
    onClick: () => setOpen((o) => !o),
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    },
    "aria-expanded": open,
    "aria-haspopup": "menu", // override per pattern (menu/listbox) in the consumer
  };

  // Extract side from placement for directional animations
  const side = currentPlacement?.split('-')[0] || placement.split('-')[0];
  
  const floatingProps = {
    style: floatingStyles,
    "data-state": open ? "open" : "closed", // useful for CSS transitions
    "data-side": side, // for directional animations
  };

  return {
    open,
    setOpen,
    refs, // refs.setReference, refs.setFloating
    floatingStyles, // inline styles for positioned element
    referenceProps, // default trigger handlers
    floatingProps, // default positioned element props
    update, // expose update() if the consumer needs it
  };
}